import {
  WorkplaceRequestElement,
  WorkplaceRequestFieldValueInput,
  WorkplaceRequestLocationValue,
  WorkplaceRequestTypeElement
} from "@gettactic/api";
import { reservationFieldConfig } from "../workplace-requests/reservationField";
import { toWorkplaceRequestFieldValue } from "../workplace-requests/toWorkplaceRequestFieldValue";
import { toWorkplaceRequestFormValue } from "../workplace-requests/toWorkplaceRequestFormValue";

export interface WorkplaceRequestReserveFormData {
  id: string | undefined;
  type: WorkplaceRequestTypeElement;
  title: string;
  description: string;
  isDeleted: boolean;
  originalData: WorkplaceRequestElement;
  /** Office the request is filed for (defaults to the reservation office on save). */
  office_id?: string;
  /** Ticket-level location the request targets, when applicable. */
  location?: WorkplaceRequestLocationValue;
  /** Dynamic field answers, in the shape the create/update API expects. */
  field_values?: WorkplaceRequestFieldValueInput[];
  /**
   * Raw react-hook-form values, so the wizard can re-hydrate every field (including
   * dynamic ones) when the request is edited from the reservation list.
   */
  values?: Record<string, unknown>;
}

export const mapWorkplaceRequestToReserveFormData = (
  workplaceRequests: WorkplaceRequestElement[],
  types: WorkplaceRequestTypeElement[],
  includeIds = true
): WorkplaceRequestReserveFormData[] => {
  return workplaceRequests.map((req) => {
    const type = types.find((t) => t.id === req.type.id);
    const fieldValues: WorkplaceRequestFieldValueInput[] = (
      req.field_values ?? []
    ).map((fv) => ({
      field_id: fv.id,
      // Round-tripped rather than copied: a read answer is not always in the
      // shape a write takes (a `person` reads back as the whole user), and the
      // form shape is the one both helpers agree on.
      // The switches come from the type's own field: a field *value* carries
      // no `config` on the wire, so reading them off `fv` would say "nothing
      // is allowed" and quietly drop the days, repeat and guests it holds.
      value: toWorkplaceRequestFieldValue(
        fv.field_type,
        toWorkplaceRequestFormValue(fv.field_type, fv.value),
        reservationFieldConfig(
          (type?.fields ?? []).find((field) => field.id === fv.id) ?? {}
        )
      )
    }));

    // Rebuild the raw form state the wizard expects: title/description/office are
    // first-class fields, dynamic answers are keyed by their field id.
    const values: Record<string, unknown> = {
      title: req.title,
      description: req.description ?? "",
      office_id: req.office_id
    };
    for (const fv of req.field_values ?? []) {
      values[fv.id] = toWorkplaceRequestFormValue(fv.field_type, fv.value);
    }

    return {
      id: includeIds ? req.id : undefined,
      isNew: !includeIds,
      type: type || {
        id: req.type.id,
        name: "Unknown Type",
        description: "",
        // A request already exists against this type, so it files tickets
        // whatever the (unresolvable) real type says.
        kind: "default",
        in_use: false,
        llm_context: "",
        assigned_users: [],
        category_id: "",
        office_ids: [],
        has_office_restrictions: false,
        resource_ids: [],
        has_resource_restrictions: false,
        default_auto_assignee: null,
        office_auto_assignees: {},
        statuses: [],
        fields: [],
        icon: null,
        requires_approval: false,
        close_on_approval: false,
        requires_location: false,
        require_location_at_approval: false,
        hide_description: false
      },
      title: req.title,
      description: req.description,
      isDeleted: false,
      originalData: req,
      office_id: req.office_id,
      location: req.location ?? undefined,
      field_values: fieldValues,
      values
    };
  });
};
